import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import './person-picker.css';

export interface PickerPerson {
  id: string;
  name: string;
}

/** Kleinschreibung ohne Akzente und Umlaut-Punkte, damit „mueller“, „Müller“ und „MÜLLER“ dasselbe finden */
export function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').replace(/ß/g, 'ss').toLowerCase();
}

/** Höchstens so viele Vorschläge auf einmal, bei 100 Mitarbeitern soll die Liste kurz bleiben */
export const MAX_SUGGESTIONS = 10;

/** Personen, die zur Eingabe passen: jedes getippte Wort muss irgendwo im Namen vorkommen. Schon Gewählte fehlen. */
export function suggestions(options: PickerPerson[], selected: string[], input: string): PickerPerson[] {
  const words = fold(input).split(/\s+/).filter(Boolean);
  return options
    .filter((p) => !selected.includes(p.id))
    .filter((p) => words.every((w) => fold(p.name).includes(w)))
    .slice(0, MAX_SUGGESTIONS);
}

interface Props {
  label: string;
  options: PickerPerson[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Eigene ID: Die eigene Person bekommt den Zusatz „(ich)“ */
  selfId?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
}

/**
 * Auswahl mehrerer Personen über ein Suchfeld. Beim Tippen erscheinen passende Personen, die gewählten stehen als Schilder darüber und lassen sich einzeln entfernen.
 * Mit der Tastatur: Pfeiltasten wählen, Eingabe übernimmt, Escape schließt, Rücktaste im leeren Feld entfernt die zuletzt gewählte Person.
 */
export function PersonPicker({ label, options, value, onChange, disabled, selfId, hint, error, placeholder = 'Name eingeben …' }: Props) {
  const uid = useId();
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const field = useRef<HTMLInputElement>(null);

  const found = useMemo(() => suggestions(options, value, input), [options, value, input]);
  const nameOf = (id: string) => options.find((p) => p.id === id)?.name ?? null;
  const withSelf = (p: PickerPerson) => (p.id === selfId ? `${p.name} (ich)` : p.name);

  const add = (id: string) => {
    onChange([...value, id]);
    setInput('');
    setActive(0);
    field.current?.focus();
  };
  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, found.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // Eingabe im Suchfeld darf das Formular nicht abschicken
      e.preventDefault();
      if (open && found[active]) add(found[active].id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && input === '' && value.length > 0) {
      remove(value[value.length - 1]!);
    }
  }

  const listId = `${uid}-list`;
  const showList = open && !disabled && (input !== '' || found.length > 0);

  return (
    <div className="field picker">
      <span id={`${uid}-label`} className="picker__label">{label}</span>
      {value.length > 0 && (
        <ul className="picker__chips" aria-label={`Ausgewählt: ${label}`}>
          {value.map((id) => {
            const name = nameOf(id);
            return (
              <li key={id} className={`picker__chip${name ? '' : ' picker__chip--gone'}`}>
                <span>{name ? withSelf({ id, name }) : 'Nicht mehr verfügbar'}</span>
                {!disabled && (
                  <button type="button" className="picker__remove" onClick={() => remove(id)} aria-label={`${name ?? 'Person'} entfernen`}>×</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!disabled && (
        <div className="picker__box">
          <input
            ref={field}
            className="input"
            type="text"
            role="combobox"
            aria-labelledby={`${uid}-label`}
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && found[active] ? `${uid}-opt-${found[active].id}` : undefined}
            aria-invalid={!!error}
            autoComplete="off"
            placeholder={placeholder}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setOpen(true);
              setActive(0);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKey}
          />
          {showList && (
            <ul id={listId} className="picker__list" role="listbox" aria-label={`Vorschläge: ${label}`}>
              {found.length === 0 && <li className="picker__empty">Niemand gefunden</li>}
              {found.map((p, i) => (
                <li
                  key={p.id}
                  id={`${uid}-opt-${p.id}`}
                  role="option"
                  aria-selected={i === active}
                  className={`picker__option${i === active ? ' is-active' : ''}`}
                  // mousedown statt click, damit das Feld den Fokus behält und die Liste nicht vorher schließt
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(p.id);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  {withSelf(p)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && <small className="field__error" role="alert">{error}</small>}
      {hint && <small className="muted">{hint}</small>}
    </div>
  );
}
